import type {
  MetadataResponse,
  MetadataUserDataSavedPlaceResponse,
  MetadataUserDataSettingsResponse,
} from '../../models/metadata-response';
import type { MaterialsResponse, RawAnswerResponse, RawMaterialResponse } from '../../models/quiz';
import type { SelectionResponse } from '../../models/selections';
import type { OfflineSnapshot, OutboxBody, OutboxFormEntry, OutboxOp } from './offline-db';

/** Writes the reader can do offline; anything else needs connection. */
export type OfflineWriteKind =
  | 'selection'
  | 'position'
  | 'settings'
  | 'material'
  | 'answer'
  | 'reading';
export type OfflineWriteAction = 'create' | 'update' | 'delete' | 'finish';

export interface OfflineWriteRoute {
  kind: OfflineWriteKind;
  action: OfflineWriteAction;
  /** Id in the URL (`/selections/:id`, `/readingMaterial/:id`, `/answers/finish/:id`). */
  targetId?: string;
}

type Payload = Record<string, unknown>;

export const TEMP_ID_PREFIX = 'offline-';
/** Reading sessions made offline, sent together as NR1 does. */
export const OFFLINE_READINGS_URL = '/nubereader/statistics/offline';
/** Multipart fields that carry the JSON payload of reading material and voice answers. */
const PAYLOAD_FIELDS = ['exercise', 'answer'];

const ROUTES: readonly {
  method: string;
  pattern: RegExp;
  route: (id?: string) => OfflineWriteRoute;
}[] = [
  {
    method: 'POST',
    pattern: /^\/nubereader\/selections$/u,
    route: () => ({ kind: 'selection', action: 'create' }),
  },
  {
    method: 'PUT',
    pattern: /^\/nubereader\/selections\/([^/]+)$/u,
    route: (id) => ({ kind: 'selection', action: 'update', targetId: id }),
  },
  {
    method: 'DELETE',
    pattern: /^\/nubereader\/selections\/([^/]+)$/u,
    route: (id) => ({ kind: 'selection', action: 'delete', targetId: id }),
  },
  {
    method: 'POST',
    pattern: /^\/nubereader\/saveLastPageRead$/u,
    route: () => ({ kind: 'position', action: 'update' }),
  },
  {
    method: 'PUT',
    pattern: /^\/nubereader\/booksUsersData$/u,
    route: () => ({ kind: 'settings', action: 'update' }),
  },
  {
    method: 'POST',
    pattern: /^\/nubereader\/readingMaterial\/answers$/u,
    route: () => ({ kind: 'answer', action: 'create' }),
  },
  {
    method: 'POST',
    pattern: /^\/nubereader\/readingMaterial\/answers\/finish\/([^/]+)$/u,
    route: (id) => ({ kind: 'answer', action: 'finish', targetId: id }),
  },
  {
    method: 'POST',
    pattern: /^\/nubereader\/readingMaterial$/u,
    route: () => ({ kind: 'material', action: 'create' }),
  },
  {
    method: 'PUT',
    pattern: /^\/nubereader\/readingMaterial\/([^/]+)$/u,
    route: (id) => ({ kind: 'material', action: 'update', targetId: id }),
  },
  {
    method: 'DELETE',
    pattern: /^\/nubereader\/readingMaterial\/([^/]+)$/u,
    route: (id) => ({ kind: 'material', action: 'delete', targetId: id }),
  },
  {
    method: 'POST',
    pattern: /^\/nubereader\/statistics$/u,
    route: () => ({ kind: 'reading', action: 'create' }),
  },
  {
    method: 'PUT',
    pattern: /^\/nubereader\/statistics\/([^/]+)$/u,
    route: (id) => ({ kind: 'reading', action: 'update', targetId: id }),
  },
];

export function matchOfflineWrite(method: string, path: string): OfflineWriteRoute | undefined {
  for (const { method: routeMethod, pattern, route } of ROUTES) {
    const match = routeMethod === method ? pattern.exec(path) : null;
    if (match) return route(match[1] && decodeURIComponent(match[1]));
  }
  return undefined;
}

export function isTempId(id: unknown): id is string {
  return typeof id === 'string' && id.startsWith(TEMP_ID_PREFIX);
}

export function newTempId(): string {
  return `${TEMP_ID_PREFIX}${crypto.randomUUID()}`;
}

function isPayload(value: unknown): value is Payload {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasId(value: unknown): boolean {
  return isPayload(value) && typeof value['id'] === 'string';
}

function isSelection(value: unknown): value is SelectionResponse {
  return hasId(value);
}

function isMaterial(value: unknown): value is RawMaterialResponse {
  return hasId(value);
}

function isAnswer(value: unknown): value is RawAnswerResponse {
  return hasId(value);
}

function isSavedPlace(value: unknown): value is Partial<MetadataUserDataSavedPlaceResponse> {
  return isPayload(value);
}

/** A position merged from the stored one and a new save is complete enough to reopen at it. */
function isReopenablePlace(value: unknown): value is MetadataUserDataSavedPlaceResponse {
  return isPayload(value) && (isPayload(value['locator']) || typeof value['progress'] === 'number');
}

function isSettings(value: unknown): value is MetadataUserDataSettingsResponse {
  return isPayload(value);
}

function parsePayload(text: string): Payload | undefined {
  try {
    const value: unknown = JSON.parse(text);
    return isPayload(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/** Body as it can be stored in IndexedDB (FormData cannot), plus its JSON payload. */
export function serializeBody(body: unknown): { body: OutboxBody; payload: Payload } {
  if (body instanceof FormData) {
    const entries: OutboxFormEntry[] = [];
    let payload: Payload = {};
    body.forEach((value, name) => {
      if (typeof value === 'string') {
        entries.push({ name, value });
        if (PAYLOAD_FIELDS.includes(name)) payload = parsePayload(value) ?? payload;
      } else {
        entries.push({ name, blob: value, filename: value.name });
      }
    });
    return { body: { kind: 'form', entries }, payload };
  }
  if (body === null || body === undefined) return { body: { kind: 'none' }, payload: {} };
  return { body: { kind: 'json', value: body }, payload: isPayload(body) ? body : {} };
}

export function deserializeBody(body: OutboxBody): unknown {
  if (body.kind === 'none') return null;
  if (body.kind === 'json') return body.value;
  const form = new FormData();
  body.entries.forEach((entry) => {
    if ('blob' in entry) form.append(entry.name, entry.blob, entry.filename);
    else form.append(entry.name, entry.value);
  });
  return form;
}

/** Same op with every occurrence of a temporary id replaced by the server one. */
export function replaceId(op: OutboxOp, from: string, to: string): OutboxOp {
  const swap = (text: string): string => text.split(from).join(to);
  let body: OutboxBody = op.body;
  if (body.kind === 'json') {
    body = { kind: 'json', value: JSON.parse(swap(JSON.stringify(body.value))) as unknown };
  } else if (body.kind === 'form') {
    body = {
      kind: 'form',
      entries: body.entries.map((entry) =>
        'blob' in entry ? entry : { name: entry.name, value: swap(entry.value) },
      ),
    };
  }
  return { ...op, url: swap(op.url), body, tempId: op.tempId === from ? to : op.tempId };
}

function references(op: OutboxOp, id: string): boolean {
  return (
    op.url.includes(id) ||
    (op.body.kind === 'json' && JSON.stringify(op.body.value).includes(id)) ||
    (op.body.kind === 'form' &&
      op.body.entries.some((entry) => 'value' in entry && entry.value.includes(id)))
  );
}

export interface EnqueuePlan {
  /** Pending ops (by seq) that the new one makes obsolete. */
  remove: number[];
  /** Pending op that absorbs the new one instead of adding it. */
  replace?: OutboxOp;
  add?: OutboxOp;
}

/**
 * Folds a new offline write into the pending queue: only the last position/settings is kept, an
 * update of something created offline rewrites that create, and deleting something created
 * offline drops it and every op that depends on it (nothing reaches the server).
 */
export function planEnqueue(
  pending: OutboxOp[],
  op: OutboxOp,
  route: OfflineWriteRoute,
): EnqueuePlan {
  const seqs = (ops: OutboxOp[]): number[] =>
    ops.map((pendingOp) => pendingOp.seq).filter((seq): seq is number => seq !== undefined);

  if (op.coalesceKey) {
    return { remove: seqs(pending.filter((p) => p.coalesceKey === op.coalesceKey)), add: op };
  }
  const { targetId } = route;
  const create = isTempId(targetId) ? pending.find((p) => p.tempId === targetId) : undefined;
  if (!create || !targetId) return { remove: [], add: op };

  if (route.action === 'delete') {
    return {
      remove: seqs(pending.filter((p) => p === create || references(p, targetId))),
      add: undefined,
    };
  }
  if (route.action === 'update') {
    return { remove: [], replace: { ...create, body: op.body } };
  }
  return { remove: [], add: op };
}

function readingOf(op: OutboxOp): Payload | undefined {
  const value = op.body.kind === 'json' ? op.body.value : undefined;
  return Array.isArray(value) && isPayload(value[0]) ? value[0] : undefined;
}

/**
 * Body of the `statistics/offline` op for a reading session: opening one records when it started,
 * and each update of it (one per page turn) where and when it ended. An update of a session that
 * is not queued gives nothing to send.
 */
export function readingBody(
  route: OfflineWriteRoute,
  payload: Payload,
  context: { pending: OutboxOp[]; now: number },
): OutboxBody | undefined {
  const { pending, now } = context;
  const nowIso = new Date(now).toISOString();
  if (route.action === 'create') {
    return { kind: 'json', value: [{ timezone: payload['timezone'], dateIn: nowIso }] };
  }
  const create = pending.find((op) => isTempId(route.targetId) && op.tempId === route.targetId);
  const reading = create && readingOf(create);
  if (!reading) return undefined;
  return {
    kind: 'json',
    value: [
      {
        ...reading,
        dateOut: nowIso,
        readingPercent: payload['readingPercent'],
        ebookReadingPercentage: payload['ebookReadingPercentage'],
      },
    ],
  };
}

function findMaterial(snapshot: OfflineSnapshot, id?: string): RawMaterialResponse | undefined {
  return snapshot.materials?.questions.find((question) => question.id === id);
}

function findAnswer(snapshot: OfflineSnapshot, id?: string): RawAnswerResponse | undefined {
  return snapshot.materials?.answers.find((answer) => answer.id === id);
}

/** Response the API would give, so the reader keeps working without it. */
export function syntheticResponse(
  route: OfflineWriteRoute,
  payload: Payload,
  context: { tempId?: string; snapshot: OfflineSnapshot; metadata: MetadataResponse; now: number },
): unknown {
  const { tempId, snapshot, metadata, now } = context;
  const nowIso = new Date(now).toISOString();
  switch (route.kind) {
    case 'selection':
      if (route.action === 'create') {
        return {
          ...payload,
          id: tempId,
          userId: metadata.userData.userId,
          libraryId: metadata.otkId,
          bookId: metadata.bookId,
          createDate: now,
        };
      }
      if (route.action === 'update') {
        const current = snapshot.selections.find((s) => s.id === route.targetId);
        return { ...current, ...payload, id: route.targetId };
      }
      return null;
    case 'position':
      return {};
    case 'settings':
      return 'ok';
    case 'material':
      if (route.action === 'delete') return {};
      return {
        ...findMaterial(snapshot, route.targetId),
        ...payload,
        id: route.targetId ?? tempId,
        created: nowIso,
        updated: nowIso,
      };
    case 'answer': {
      if (route.action === 'finish') {
        return { ...findAnswer(snapshot, route.targetId), finished: true, pendingSync: true };
      }
      const id = typeof payload['id'] === 'string' ? payload['id'] : tempId;
      return {
        ...findAnswer(snapshot, id),
        ...payload,
        id,
        userId: metadata.userData.userId,
        attempt: payload['attempt'] ?? 1,
        created: nowIso,
        updated: nowIso,
        // Graded by the server: shown as pending until synced.
        pendingSync: true,
      };
    }
    case 'reading':
      return route.action === 'create' ? { id: tempId } : '';
    default:
      return {};
  }
}

function upsertById<T extends { id: string }>(items: T[], item: T): T[] {
  return items.some((existing) => existing.id === item.id)
    ? items.map((existing) => (existing.id === item.id ? item : existing))
    : [...items, item];
}

export interface AppliedWrite {
  route: OfflineWriteRoute;
  payload: Payload;
  /** Real response online, synthetic offline. */
  response: unknown;
  now: number;
}

/** Snapshot after a write. */
export function applyWrite(snapshot: OfflineSnapshot, write: AppliedWrite): OfflineSnapshot {
  const { route, payload, response, now } = write;
  const materials: MaterialsResponse = snapshot.materials ?? { questions: [], answers: [] };
  switch (route.kind) {
    case 'selection': {
      if (route.action === 'delete') {
        return {
          ...snapshot,
          selections: snapshot.selections.filter((s) => s.id !== route.targetId),
        };
      }
      return isSelection(response)
        ? { ...snapshot, selections: upsertById(snapshot.selections, response) }
        : snapshot;
    }
    case 'position': {
      const { savedPlace } = payload;
      if (!isSavedPlace(savedPlace)) return snapshot;
      const dateLastRead =
        typeof savedPlace.dateLastRead === 'number' ? savedPlace.dateLastRead : now;
      const merged = { ...snapshot.savedPlace, ...savedPlace, dateLastRead };
      return isReopenablePlace(merged) ? { ...snapshot, savedPlace: merged } : snapshot;
    }
    case 'settings': {
      const { settings } = payload;
      return isSettings(settings) ? { ...snapshot, settings } : snapshot;
    }
    case 'material': {
      if (route.action === 'delete') {
        return {
          ...snapshot,
          materials: {
            ...materials,
            questions: materials.questions.filter((q) => q.id !== route.targetId),
          },
        };
      }
      return isMaterial(response)
        ? {
            ...snapshot,
            materials: { ...materials, questions: upsertById(materials.questions, response) },
          }
        : snapshot;
    }
    case 'answer':
      return isAnswer(response)
        ? {
            ...snapshot,
            materials: { ...materials, answers: upsertById(materials.answers, response) },
          }
        : snapshot;
    // Reading sessions are not part of the copy.
    case 'reading':
    default:
      return snapshot;
  }
}

/** Snapshot without what a change the server rejected had created: it was never saved. */
export function withoutCreated(snapshot: OfflineSnapshot, tempId: string): OfflineSnapshot {
  const { materials } = snapshot;
  return {
    ...snapshot,
    selections: snapshot.selections.filter((selection) => selection.id !== tempId),
    materials: materials && {
      questions: materials.questions.filter((question) => question.id !== tempId),
      answers: materials.answers.filter((answer) => answer.id !== tempId),
    },
  };
}

/** Snapshot of the reader data in a metadata response. */
export function snapshotOf(
  loanKey: string,
  metadata: MetadataResponse,
  selections: SelectionResponse[],
): OfflineSnapshot {
  return {
    loanKey,
    selections,
    savedPlace: metadata.userData.savedPlace,
    settings: metadata.userData.settings,
    materials: metadata.materials,
  };
}

/**
 * Downloaded metadata with the configuration the server sends now (loan config, reader plugins
 * and flags, user permissions), which may change after the download. The book itself stays.
 */
export function withLatestConfig(
  downloaded: MetadataResponse,
  latest: MetadataResponse,
): MetadataResponse {
  return {
    ...downloaded,
    config: latest.config,
    userConfig: latest.userConfig,
    metadata: { ...downloaded.metadata, readerConfig: latest.metadata.readerConfig },
  };
}

/** Downloaded metadata with the reader data of the snapshot (offline changes included). */
export function withSnapshot(
  metadata: MetadataResponse,
  snapshot?: OfflineSnapshot,
): MetadataResponse {
  if (!snapshot) return metadata;
  return {
    ...metadata,
    userData: {
      ...metadata.userData,
      savedPlace: snapshot.savedPlace,
      settings: snapshot.settings,
    },
    materials: snapshot.materials,
  };
}
